// Data management module for PPT Hero game
export default class DataManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Save game data
  saveGameData() {
    try {
      const gameData = {
        score: this.scene.score || 0,
        currentLevel: this.scene.currentLevel || 0,
        currentRound: this.scene.currentRound || 'practice',
        roundProgress: this.scene.roundProgress || {},
        analyticsData: this.scene.analyticsData || {},
        settings: this.scene.settingsManager ? this.scene.settingsManager.getAllSettings() : {},
        timestamp: Date.now()
      };

      localStorage.setItem('pptHeroGameData', JSON.stringify(gameData));
      console.log('Game data saved successfully');
      return true;
      
    } catch (error) {
      console.error('Error saving game data:', error);
      return false;
    }
  }

  // Load game data
  loadGameData() {
    try {
      const savedData = localStorage.getItem('pptHeroGameData');
      if (savedData) {
        const gameData = JSON.parse(savedData);
        
        // Restore game state
        if (gameData.score !== undefined) {
          this.scene.score = gameData.score;
        }
        if (gameData.currentLevel !== undefined) {
          this.scene.currentLevel = gameData.currentLevel;
        }
        if (gameData.currentRound !== undefined) {
          this.scene.currentRound = gameData.currentRound;
        }
        if (gameData.roundProgress) {
          this.scene.roundProgress = gameData.roundProgress;
        }
        if (gameData.analyticsData) {
          this.scene.analyticsData = gameData.analyticsData;
        }
        
        console.log('Game data loaded successfully');
        return true;
      }
      
      console.log('No saved game data found');
      return false;
      
    } catch (error) {
      console.error('Error loading game data:', error);
      return false;
    }
  }

  // Export all game data
  exportAllData() {
    try {
      const exportData = {
        gameData: {
          score: this.scene.score || 0,
          currentLevel: this.scene.currentLevel || 0,
          currentRound: this.scene.currentRound || 'practice',
          roundProgress: this.scene.roundProgress || {},
          analyticsData: this.scene.analyticsData || {}
        },
        settings: this.scene.settingsManager ? this.scene.settingsManager.exportSettings() : {},
        progress: this.scene.progressManager ? this.scene.progressManager.exportProgress() : {},
        analytics: this.scene.analyticsManager ? this.scene.analyticsManager.exportAnalytics() : {},
        exportDate: new Date().toISOString(),
        version: '1.0.0'
      };

      // Create downloadable file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = `ppt-hero-data-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      console.log('All game data exported successfully');
      return true;
      
    } catch (error) {
      console.error('Error exporting game data:', error);
      return false;
    }
  }

  // Import game data
  importGameData(data) {
    try {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid data format');
      }

      let importSuccess = false;

      // Import game data
      if (data.gameData) {
        if (this.loadGameDataFromObject(data.gameData)) {
          importSuccess = true;
        }
      }

      // Import settings
      if (data.settings && this.scene.settingsManager) {
        if (this.scene.settingsManager.importSettings(data.settings)) {
          importSuccess = true;
        }
      }

      // Import progress
      if (data.progress && this.scene.progressManager) {
        if (this.scene.progressManager.importProgress(data.progress)) {
          importSuccess = true;
        }
      }

      // Import analytics
      if (data.analytics && this.scene.analyticsManager) {
        if (this.scene.analyticsManager.importAnalytics(data.analytics)) {
          importSuccess = true;
        }
      }

      if (importSuccess) {
        console.log('Game data imported successfully');
        this.scene.showCenterNotification('Game data imported successfully', 'success', 3000);
        return true;
      } else {
        throw new Error('No valid data found to import');
      }
      
    } catch (error) {
      console.error('Error importing game data:', error);
      this.scene.showCenterNotification('Error importing game data', 'error', 3000);
      return false;
    }
  }

  // Load game data from object
  loadGameDataFromObject(gameData) {
    try {
      if (!gameData) return false;

      // Restore game state
      if (gameData.score !== undefined) {
        this.scene.score = gameData.score;
      }
      if (gameData.currentLevel !== undefined) {
        this.scene.currentLevel = gameData.currentLevel;
      }
      if (gameData.currentRound !== undefined) {
        this.scene.currentRound = gameData.currentRound;
      }
      if (gameData.roundProgress) {
        this.scene.roundProgress = gameData.roundProgress;
      }
      if (gameData.analyticsData) {
        this.scene.analyticsData = gameData.analyticsData;
      }

      return true;
      
    } catch (error) {
      console.error('Error loading game data from object:', error);
      return false;
    }
  }

  // Clear all game data
  clearAllData() {
    try {
      // Clear localStorage items
      localStorage.removeItem('pptHeroGameData');
      localStorage.removeItem('pptHeroSettings');
      localStorage.removeItem('pptHeroRoundProgress');
      localStorage.removeItem('pptHeroAnalytics');
      localStorage.removeItem('pptHeroHighScore');
      localStorage.removeItem('pptHeroCalibrationBaseline');

      // Reset scene variables
      this.scene.score = 0;
      this.scene.currentLevel = 0;
      this.scene.currentRound = 'practice';
      this.scene.roundProgress = {};
      this.scene.analyticsData = null;

      // Reset managers if they exist
      if (this.scene.settingsManager) {
        this.scene.settingsManager.resetSettings();
      }
      if (this.scene.progressManager) {
        this.scene.progressManager.resetAllProgress();
      }
      if (this.scene.analyticsManager) {
        this.scene.analyticsManager.clearAnalytics();
      }

      console.log('All game data cleared successfully');
      this.scene.showCenterNotification('All game data cleared', 'info', 3000);
      return true;
      
    } catch (error) {
      console.error('Error clearing game data:', error);
      return false;
    }
  }

  // Get data summary
  getDataSummary() {
    try {
      const summary = {
        score: this.scene.score || 0,
        currentLevel: this.scene.currentLevel || 0,
        currentRound: this.scene.currentRound || 'practice',
        totalProgress: this.scene.roundProgress ? Object.keys(this.scene.roundProgress).length : 0,
        totalAnalytics: this.scene.analyticsData ? Object.keys(this.scene.analyticsData).length : 0,
        lastSaved: this.getLastSavedTime(),
        dataSize: this.getDataSize()
      };

      return summary;
      
    } catch (error) {
      console.error('Error getting data summary:', error);
      return null;
    }
  }

  // Get last saved time
  getLastSavedTime() {
    try {
      const savedData = localStorage.getItem('pptHeroGameData');
      if (savedData) {
        const gameData = JSON.parse(savedData);
        return gameData.timestamp ? new Date(gameData.timestamp).toLocaleString() : 'Unknown';
      }
      return 'Never';
    } catch (error) {
      return 'Unknown';
    }
  }

  // Get data size
  getDataSize() {
    try {
      let totalSize = 0;
      const keys = [
        'pptHeroGameData',
        'pptHeroSettings',
        'pptHeroRoundProgress',
        'pptHeroAnalytics',
        'pptHeroHighScore',
        'pptHeroCalibrationBaseline'
      ];

      keys.forEach(key => {
        const data = localStorage.getItem(key);
        if (data) {
          totalSize += new Blob([data]).size;
        }
      });

      // Convert to KB
      return (totalSize / 1024).toFixed(2) + ' KB';
      
    } catch (error) {
      return 'Unknown';
    }
  }

  // Validate data integrity
  validateDataIntegrity() {
    try {
      const issues = [];

      // Check game data
      const gameData = localStorage.getItem('pptHeroGameData');
      if (gameData) {
        try {
          const parsed = JSON.parse(gameData);
          if (!parsed.timestamp || !parsed.score) {
            issues.push('Game data missing required fields');
          }
        } catch (e) {
          issues.push('Game data corrupted');
        }
      }

      // Check progress data
      const progressData = localStorage.getItem('pptHeroRoundProgress');
      if (progressData) {
        try {
          const parsed = JSON.parse(progressData);
          if (typeof parsed !== 'object') {
            issues.push('Progress data format invalid');
          }
        } catch (e) {
          issues.push('Progress data corrupted');
        }
      }

      // Check analytics data
      const analyticsData = localStorage.getItem('pptHeroAnalytics');
      if (analyticsData) {
        try {
          const parsed = JSON.parse(analyticsData);
          if (typeof parsed !== 'object') {
            issues.push('Analytics data format invalid');
          }
        } catch (e) {
          issues.push('Analytics data corrupted');
        }
      }

      return {
        isValid: issues.length === 0,
        issues: issues
      };
      
    } catch (error) {
      console.error('Error validating data integrity:', error);
      return {
        isValid: false,
        issues: ['Validation error occurred']
      };
    }
  }

  // Auto-save game data
  autoSave() {
    try {
      if (this.scene.settingsManager && this.scene.settingsManager.isEnabled('autoSave')) {
        this.saveGameData();
        console.log('Auto-save completed');
      }
    } catch (error) {
      console.error('Error during auto-save:', error);
    }
  }

  // Setup auto-save interval
  setupAutoSave(intervalMinutes = 5) {
    try {
      if (this.autoSaveInterval) {
        clearInterval(this.autoSaveInterval);
      }

      this.autoSaveInterval = setInterval(() => {
        this.autoSave();
      }, intervalMinutes * 60 * 1000);

      console.log(`Auto-save set to ${intervalMinutes} minutes`);
      
    } catch (error) {
      console.error('Error setting up auto-save:', error);
    }
  }

  // Stop auto-save
  stopAutoSave() {
    try {
      if (this.autoSaveInterval) {
        clearInterval(this.autoSaveInterval);
        this.autoSaveInterval = null;
        console.log('Auto-save stopped');
      }
    } catch (error) {
      console.error('Error stopping auto-save:', error);
    }
  }

  // End session and update analytics
  endSession() {
    try {
      const user = auth.currentUser;
      const userId = user ? user.uid : null;
      
      if (userId && this.scene.sessionId) {
        console.log("Ending session", this.scene.sessionId, "for user", userId);
        
        // Find and update the session
        db.collection("sessions")
          .where("sessionId", "==", this.scene.sessionId)
          .where("userId", "==", userId)
          .get()
          .then((snapshot) => {
            if (!snapshot.empty) {
              snapshot.forEach((doc) => {
                const sessionData = doc.data();
                console.log("Found session to update:", doc.id);
                
                // Use a safer approach to get startTime
                let startTime = new Date();
                if (sessionData.startTime) {
                  // Handle both timestamp and date objects
                  startTime = typeof sessionData.startTime.toDate === 'function' 
                    ? sessionData.startTime.toDate() 
                    : sessionData.startTime;
                }
                
                const duration = (Date.now() - startTime.getTime()) / 1000; // in seconds
                
                const sessionUpdate = {
                  endTime: firebase.firestore.FieldValue.serverTimestamp(),
                  duration: duration,
                  levelsPlayed: this.scene.levelsPlayed || 0,
                  inProgress: false,
                  score: this.scene.score || 0,
                  gameType: "PPTHero"
                };
                
                console.log("Updating session with:", sessionUpdate);
                
                doc.ref.update(sessionUpdate)
                  .then(() => {
                    console.log("Session successfully updated");
                  })
                  .catch(error => {
                    console.error("Error updating session:", error);
                  });
              });
            } else {
              console.log("No active session found to end");
            }
          })
          .catch(error => {
            console.error("Error finding session to end:", error);
          });
      }
    } catch (error) {
      console.error("Error in endSession:", error);
    }
  }
}
